import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ChartDataset, ChartOptions } from 'chart.js';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { subDays, startOfYear, startOfDay, format } from 'date-fns';
import { EventStatisticsService } from '../../services/event-statistics/event-statistics.service';
import {
  EventStatisticsAggregation,
  ParsedFilterChoice,
  PredefinedEventQuery,
  PredefinedEventQueryRequest,
  SessionTimeline
} from '../../models/event-statistics';

interface KpiCard {
  label: string;
  value: number;
}

type TabId = 'overview' | 'products' | 'search' | 'users' | 'journeys';

const GRAY = '#697386';
const INDIGO = '#635bff';
const TEAL = '#3ecf8e';
const ORANGE = '#ff7a59';

const CHART_COLORS = [INDIGO, TEAL, ORANGE, '#c4b5fd', '#f59e0b', '#697386', '#06b6d4', '#ec4899'];

@Component({
  selector: 'lib-event-statistics',
  templateUrl: './event-statistics.component.html',
  styleUrls: ['./event-statistics.component.scss'],
  standalone: false
})
export class EventStatisticsComponent implements OnInit, OnDestroy {
  protected eventStatisticsService = inject(EventStatisticsService);
  protected unsubscribe$ = new Subject<void>();

  activeTab: TabId = 'overview';

  // Date range
  fromDate: Date;
  toDate: Date;
  fromDateStr: string;
  toDateStr: string;
  selectedPreset = 'LAST_30_DAYS';

  // Loading
  loading = true;

  // KPIs
  overviewKpis: KpiCard[] = [];
  cartKpis: KpiCard[] = [];

  // Overview charts
  overviewChartData: ChartDataset[] = [];
  overviewChartLabels: string[] = [];
  overviewChartOptions: ChartOptions = {};

  eventsByTypeData: ChartDataset[] = [];
  eventsByTypeLabels: string[] = [];
  eventsByTypeOptions: ChartOptions = {};

  trafficBySourceData: ChartDataset[] = [];
  trafficBySourceLabels: string[] = [];
  trafficBySourceOptions: ChartOptions = {};

  // Products tab
  conversionData: ChartDataset[] = [];
  conversionLabels: string[] = [];
  conversionOptions: ChartOptions = {};

  topProducts: EventStatisticsAggregation[] = [];
  topPages: EventStatisticsAggregation[] = [];

  // Search tab
  availableLists: string[] = [];
  selectedList = '';
  loadingSearch = false;
  topSearchTerms: EventStatisticsAggregation[] = [];
  filterChoices: ParsedFilterChoice[] = [];

  // Users tab
  topUsers: EventStatisticsAggregation[] = [];
  topCustomers: EventStatisticsAggregation[] = [];

  // Journeys tab
  generalTimelines: SessionTimeline[] = [];
  loadingGeneral = false;
  generalLimit = 10;
  canLoadMoreGeneral = false;

  sessionTimelines: SessionTimeline[] = [];
  timelineUserId = '';
  loadingTimeline = false;
  timelineLimit = 5;
  canLoadMore = false;

  ngOnInit(): void {
    const now = new Date();
    this.fromDate = startOfDay(subDays(now, 30));
    this.toDate = now;
    this.fromDateStr = format(this.fromDate, 'yyyy-MM-dd');
    this.toDateStr = format(this.toDate, 'yyyy-MM-dd');
    this.loadAllData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setTab(tab: TabId): void {
    this.activeTab = tab;
  }

  onPresetChange(value: string): void {
    this.selectedPreset = value;
    const now = new Date();
    switch (value) {
      case 'TODAY': this.fromDate = startOfDay(now); break;
      case 'LAST_7_DAYS': this.fromDate = startOfDay(subDays(now, 7)); break;
      case 'LAST_30_DAYS': this.fromDate = startOfDay(subDays(now, 30)); break;
      case 'LAST_90_DAYS': this.fromDate = startOfDay(subDays(now, 90)); break;
      case 'THIS_YEAR': this.fromDate = startOfYear(now); break;
      default: return;
    }
    this.toDate = now;
    this.fromDateStr = format(this.fromDate, 'yyyy-MM-dd');
    this.toDateStr = format(this.toDate, 'yyyy-MM-dd');
    this.loadAllData();
  }

  onFromDateChange(value: string): void {
    if (!value) return;
    this.fromDate = new Date(value);
    this.fromDateStr = value;
    this.selectedPreset = '';
    this.loadAllData();
  }

  onToDateChange(value: string): void {
    if (!value) return;
    this.toDate = new Date(value);
    this.toDateStr = value;
    this.selectedPreset = '';
    this.loadAllData();
  }

  onListChange(value: string): void {
    this.selectedList = value;
    this.loadSearchData();
  }

  loadAllData(): void {
    if (!this.fromDate || !this.toDate) return;
    this.loading = true;

    const from = this.fromDate.toISOString();
    const to = this.toDate.toISOString();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const q = (opts: any) => this.eventStatisticsService.query({ from, to, filters: [], payloadFilters: [], timezone: tz, ...opts });
    const pq = (queryName: PredefinedEventQuery, limit?: number) => {
      const body: PredefinedEventQueryRequest = { queryName, from, to, timezone: tz };
      if (limit) body.limit = limit;
      return this.eventStatisticsService.queryPredefined(body);
    };

    forkJoin({
      // KPIs
      pageViews: q({ eventType: 'pageView' }),
      uniqueUsers: q({ distinctCountField: 'userId' }),
      uniqueSessions: q({ distinctCountField: 'sessionId' }),
      productViews: q({ eventType: 'productView' }),
      cartActions: q({ eventType: 'cartQuantityChange' }),
      clearCarts: q({ eventType: 'clearCart' }),
      // Overview charts
      pageViewsByDay: pq('PAGE_VIEWS_BY_DAY'),
      sessionsByDay: pq('SESSIONS_BY_DAY'),
      eventsByType: pq('EVENTS_BY_TYPE'),
      trafficBySource: q({ groupBy: 'source' }),
      // Products tab
      productViewsByDay: q({ eventType: 'productView', groupBy: 'dateDay', sortDirection: 'asc' }),
      cartActivityByDay: pq('CART_ACTIVITY'),
      topProducts: pq('TOP_PRODUCTS', 10),
      topPages: q({ eventType: 'pageView', groupBy: 'payload.page', limit: 10, sortDirection: 'desc' }),
      // Search tab
      distinctLists: q({ eventType: 'listFiltered', groupBy: 'payload.list', sortDirection: 'desc' }),
      // Users tab
      topUsers: pq('TOP_USERS', 10),
      topCustomers: q({ groupBy: 'customerId', limit: 10, sortDirection: 'desc' }),
    }).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (r) => {
        // KPIs
        this.overviewKpis = [
          { label: 'PAGE_VIEWS', value: r.pageViews[0]?.count || 0 },
          { label: 'UNIQUE_USERS', value: r.uniqueUsers[0]?.distinctCount || 0 },
          { label: 'SESSIONS', value: r.uniqueSessions[0]?.distinctCount || 0 },
          { label: 'PRODUCT_VIEWS', value: r.productViews[0]?.count || 0 },
        ];
        this.cartKpis = [
          { label: 'CART_ACTIONS', value: r.cartActions[0]?.count || 0 },
          { label: 'CART_CLEARS', value: r.clearCarts[0]?.count || 0 },
        ];

        // Overview: combined page views + sessions chart
        this.buildOverviewChart(r.pageViewsByDay, r.sessionsByDay);

        // Events by type + traffic by source (horizontal bars)
        this.buildHorizontalBar(r.eventsByType, 'eventsByType');
        this.buildHorizontalBar(r.trafficBySource, 'trafficBySource');

        // Products tab: conversion chart
        this.buildConversionChart(r.productViewsByDay, r.cartActivityByDay);
        this.topProducts = r.topProducts;
        this.topPages = r.topPages;

        // Search tab: available lists
        this.availableLists = r.distinctLists
          .map(d => d.id)
          .filter(id => !!id && id.trim().length > 0);
        this.loadSearchData();

        // Users tab
        this.topUsers = r.topUsers;
        this.topCustomers = r.topCustomers;

        // Journeys tab: load general timeline
        this.loadGeneralTimelines();

        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  loadSearchData(): void {
    if (!this.fromDate || !this.toDate) return;
    this.loadingSearch = true;

    const from = this.fromDate.toISOString();
    const to = this.toDate.toISOString();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payloadFilters = this.selectedList
      ? [{ field: 'payload.list', values: [this.selectedList] }]
      : [];

    forkJoin({
      searchTerms: this.eventStatisticsService.query({
        from, to, eventType: 'listFiltered', groupBy: 'payload.searchTerms',
        unwindField: 'payload.searchTerms', limit: 10, sortDirection: 'desc',
        filters: [], payloadFilters, timezone: tz
      }),
      filterChoices: this.eventStatisticsService.query({
        from, to, eventType: 'listFiltered', groupBy: 'payload.selectedFilters',
        unwindField: 'payload.selectedFilters', limit: 20, sortDirection: 'desc',
        filters: [], payloadFilters, timezone: tz
      })
    }).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (r) => {
        this.topSearchTerms = r.searchTerms;
        this.filterChoices = this.parseFilterChoices(r.filterChoices);
        this.loadingSearch = false;
      },
      error: () => { this.loadingSearch = false; }
    });
  }

  loadGeneralTimelines(): void {
    if (!this.fromDate || !this.toDate) return;
    this.loadingGeneral = true;
    this.generalLimit = 10;
    this.fetchGeneralTimelines(this.generalLimit);
  }

  loadMoreGeneral(): void {
    this.generalLimit += 10;
    this.loadingGeneral = true;
    this.fetchGeneralTimelines(this.generalLimit);
  }

  protected fetchGeneralTimelines(limit: number): void {
    this.eventStatisticsService.getGeneralTimelines({
      from: this.fromDate.toISOString(),
      to: this.toDate.toISOString(),
      limit: limit + 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (timelines) => {
        this.canLoadMoreGeneral = timelines.length > limit;
        this.generalTimelines = timelines.slice(0, limit);
        this.loadingGeneral = false;
      },
      error: () => { this.loadingGeneral = false; }
    });
  }

  lookupTimeline(): void {
    if (!this.fromDate || !this.toDate || !this.timelineUserId.trim()) return;
    this.loadingTimeline = true;
    this.sessionTimelines = [];
    this.timelineLimit = 5;
    this.canLoadMore = false;
    this.fetchTimelines(this.timelineLimit);
  }

  loadMoreTimelines(): void {
    this.timelineLimit += 5;
    this.loadingTimeline = true;
    this.fetchTimelines(this.timelineLimit);
  }

  protected fetchTimelines(limit: number): void {
    this.eventStatisticsService.getSessionTimelines({
      from: this.fromDate.toISOString(),
      to: this.toDate.toISOString(),
      userId: this.timelineUserId.trim(),
      limit: limit + 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (timelines) => {
        this.canLoadMore = timelines.length > limit;
        this.sessionTimelines = timelines.slice(0, limit);
        this.loadingTimeline = false;
      },
      error: () => { this.loadingTimeline = false; }
    });
  }

  // ── Chart builders ──

  protected buildOverviewChart(pageViews: EventStatisticsAggregation[], sessions: EventStatisticsAggregation[]): void {
    const allDates = new Set<string>();
    pageViews.forEach(d => allDates.add(d.id));
    sessions.forEach(d => allDates.add(d.id));
    const sorted = Array.from(allDates).sort();
    const pvMap = new Map(pageViews.map(d => [d.id, d.count]));
    const sMap = new Map(sessions.map(d => [d.id, d.distinctCount || 0]));

    this.overviewChartLabels = sorted;
    this.overviewChartData = [
      this.lineDataset('Page Views', sorted.map(d => pvMap.get(d) || 0), INDIGO),
      this.lineDataset('Sessions', sorted.map(d => sMap.get(d) || 0), TEAL),
    ];
    this.overviewChartOptions = this.lineOptions(true);
  }

  protected buildConversionChart(productViews: EventStatisticsAggregation[], cartActivity: EventStatisticsAggregation[]): void {
    const allDates = new Set<string>();
    productViews.forEach(d => allDates.add(d.id));
    cartActivity.forEach(d => allDates.add(d.id));
    const sorted = Array.from(allDates).sort();
    const pvMap = new Map(productViews.map(d => [d.id, d.count]));
    const cMap = new Map(cartActivity.map(d => [d.id, d.count]));

    this.conversionLabels = sorted;
    this.conversionData = [
      this.lineDataset('Product Views', sorted.map(d => pvMap.get(d) || 0), INDIGO),
      this.lineDataset('Cart Adds', sorted.map(d => cMap.get(d) || 0), ORANGE),
    ];
    this.conversionOptions = this.lineOptions(true);
  }

  protected buildHorizontalBar(data: EventStatisticsAggregation[], target: 'eventsByType' | 'trafficBySource'): void {
    const chartData: ChartDataset[] = [{
      data: data.map(d => d.count),
      backgroundColor: CHART_COLORS.slice(0, data.length),
      borderRadius: 3,
      borderSkipped: false,
      barThickness: 18,
    }];
    const labels = data.map(d => d.id || '(unknown)');
    const options: ChartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x.toLocaleString()}` } }
      },
      scales: {
        x: { display: false },
        y: { grid: { display: false }, ticks: { font: { size: 12 }, color: '#697386' } }
      }
    };

    if (target === 'eventsByType') {
      this.eventsByTypeData = chartData;
      this.eventsByTypeLabels = labels;
      this.eventsByTypeOptions = options;
    } else {
      this.trafficBySourceData = chartData;
      this.trafficBySourceLabels = labels;
      this.trafficBySourceOptions = options;
    }
  }

  protected lineDataset(label: string, data: number[], color: string): ChartDataset {
    return {
      data, label, borderColor: color, backgroundColor: color,
      pointBackgroundColor: color, pointBorderColor: color,
      borderWidth: 1.5, pointRadius: 0, pointHoverRadius: 3,
      tension: 0.3, fill: false
    };
  }

  protected lineOptions(showLegend: boolean): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: showLegend
          ? { position: 'top', align: 'end', labels: { boxWidth: 8, usePointStyle: true, padding: 16, font: { size: 11 } } }
          : { display: false },
        tooltip: {
          mode: 'index', intersect: false,
          callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}` }
        }
      },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 }, color: '#697386' }, grid: { color: '#f0f2f5', drawBorder: false } },
        x: { grid: { display: false, drawBorder: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10, font: { size: 10 }, color: '#697386' } }
      }
    };
  }

  protected parseFilterChoices(data: EventStatisticsAggregation[]): ParsedFilterChoice[] {
    return data
      .map(item => {
        try {
          const parsed = JSON.parse(item.id);
          return { filterName: parsed.filterName || '', filterValue: parsed.filterValue || '', count: item.count };
        } catch {
          return { filterName: item.id || '', filterValue: '', count: item.count };
        }
      })
      .filter(item => item.filterName.trim().length > 0 || item.filterValue.trim().length > 0);
  }
}
